import type { RoleKey } from '@/features/auth'
import type { EmployeeRepository } from '../../application/EmployeeRepository'
import { employeeIdentifier } from '../../domain/employeeIdentifier'
import {
  EmployeeCommandError,
  employeeBusinessRoles,
  type AccessLogEntry,
  type AssignableRoute,
  type EmployeeHistoryEntry,
  type EmployeeListItem,
  type EmployeeRoutes,
  type EquipmentItem,
  type RolePermissionsMatrix,
} from '../../domain/employee'

/** Données de démonstration (mode fixtures) : personnes et équipements fictifs, jamais des données réelles. */
const routes: AssignableRoute[] = [
  { id: 'route-bleu', name: 'Route Bleu', color: '#3B82F6' },
  { id: 'route-ambre', name: 'Route Ambre', color: '#F5B82E' },
  { id: 'route-vert', name: 'Route Vert', color: '#22C55E' },
  { id: 'route-violet', name: 'Route Violet', color: '#8B5CF6' },
  { id: 'route-turquoise', name: 'Route Turquoise', color: '#14B8A6' },
]

function initialEquipments(): EquipmentItem[] {
  return [
    {
      id: 'eq-1',
      number: 'EQ-101',
      name: 'Tracteur démo A',
      category: 'Tracteur',
      status: 'disponible',
      active: true,
      available: true,
    },
    {
      id: 'eq-2',
      number: 'EQ-102',
      name: 'Tracteur démo B',
      category: 'Tracteur',
      status: 'disponible',
      active: true,
      available: true,
    },
    {
      id: 'eq-3',
      number: 'EQ-103',
      name: 'Tracteur démo C',
      category: 'Tracteur',
      status: 'brise',
      active: true,
      available: false,
    },
    {
      id: 'eq-4',
      number: 'EQ-104',
      name: 'Camionnette démo',
      category: 'Camion',
      status: 'disponible',
      active: false,
      available: false,
    },
  ]
}

function routeRef(id: string, available = true) {
  const route = routes.find((candidate) => candidate.id === id)
  return route ? { ...route, season: '2026-2027', available } : null
}

function equipmentRef(item: EquipmentItem) {
  return {
    id: item.id,
    number: item.number,
    name: item.name,
    status: item.status,
    active: item.active,
    available: item.available,
  }
}

function person(
  key: string,
  prenom: string | null,
  nom: string | null,
  identifier: string | null,
  roles: RoleKey[],
  extra: Partial<EmployeeListItem> = {},
): EmployeeListItem {
  return {
    key,
    userId: identifier ? key : null,
    employeeId: prenom ? `emp-${key}` : null,
    prenom,
    nom,
    displayName: prenom && nom ? `${prenom} ${nom}` : (identifier ?? key),
    identifier,
    identityLocked: Boolean(prenom),
    identityToComplete: Boolean(identifier) && !prenom,
    roles,
    status: identifier ? 'ACTIVE' : 'NO_ACCOUNT',
    mustChangePassword: false,
    lastLoginAt: '2026-10-10T13:42:00.000Z',
    route: null,
    equipment: null,
    ...extra,
  }
}

function initialEmployees(equipments: EquipmentItem[]): EmployeeListItem[] {
  const [first, second, broken] = equipments as [EquipmentItem, EquipmentItem, EquipmentItem]
  return [
    person('00000000-0000-4000-b000-000000000001', 'Jean-François', 'Roy', 'admin@reca.local', [
      'ADMINISTRATOR',
    ]),
    person('user-dispatch', 'Camille', 'Gagnon', 'c.gagnon@groupereca.com', ['DISPATCHER']),
    person('user-op-1', 'Élise', 'Côté-Brière', 'e.cote-briere@groupereca.com', ['OPERATOR'], {
      route: routeRef('route-bleu'),
      equipment: equipmentRef(first),
    }),
    person(
      'user-op-2',
      'Marc',
      'Saint-Pierre Tremblay',
      'm.saint-pierre-tremblay@groupereca.com',
      ['OPERATOR'],
      {
        route: routeRef('route-ambre'),
        equipment: equipmentRef(first),
        lastLoginAt: null,
        mustChangePassword: true,
      },
    ),
    person('user-op-3', 'Luc', 'D’Amours', 'l.damours@groupereca.com', ['OPERATOR'], {
      route: routeRef('route-vert'),
      equipment: equipmentRef(broken),
    }),
    person('user-op-4', 'Ana', 'Lima', 'a.lima@groupereca.com', ['OPERATOR'], {
      equipment: equipmentRef(second),
    }),
    person('user-balises', 'Sam', 'Ouellet', 'balises@groupereca.com', ['FIELD_MARKER']),
    person('user-legacy', null, null, 'ancien.compte@reca.local', ['VIEWER']),
    person('user-disabled', 'Paul', 'Bastien', 'p.bastien@groupereca.com', ['OPERATOR'], {
      status: 'DISABLED',
      lastLoginAt: '2026-03-02T11:05:00.000Z',
    }),
  ]
}

const permissionCatalog = [
  ['dashboard.read', 'Consulter le Centre des opérations.'],
  ['route.read', 'Consulter les routes.'],
  ['route.create', 'Créer une route.'],
  ['route.update', 'Modifier une route.'],
  ['marker.read', 'Consulter les balises.'],
  ['marker.update', 'Confirmer une installation de balises.'],
  ['assigned_mission.read', 'Consulter uniquement une mission assignée.'],
  ['employee.read', 'Consulter les employés autorisés.'],
  ['employee.manage', 'Créer les comptes employés, réinitialiser un mot de passe, activer ou désactiver.'],
  ['employee.assign', 'Modifier la route et l’équipement assignés à un employé.'],
  ['equipment.read', 'Consulter les équipements.'],
  ['equipment.manage', 'Gérer le référentiel minimal des équipements.'],
  ['security_log.read', 'Consulter le journal d’accès et l’historique administratif.'],
  ['users.manage', 'Gérer les comptes utilisateurs.'],
  ['roles.manage', 'Gérer les rôles et permissions.'],
  ['settings.read', 'Consulter les paramètres.'],
  ['settings.manage', 'Modifier les paramètres.'],
] as const

const protectedPermissions = [
  'users.manage',
  'roles.manage',
  'employee.read',
  'employee.manage',
  'employee.assign',
  'security_log.read',
  'settings.read',
  'settings.manage',
]

function initialMatrix(): RolePermissionsMatrix {
  return {
    roles: [
      {
        key: 'ADMINISTRATOR',
        label: 'Administrateur',
        description: 'Administration complète de l’organisation.',
        permissions: permissionCatalog.map(([key]) => key),
        memberCount: 1,
      },
      {
        key: 'DISPATCHER',
        label: 'Répartiteur',
        description: 'Planification, routes, missions et assignations.',
        permissions: [
          'dashboard.read',
          'route.read',
          'route.create',
          'route.update',
          'employee.read',
          'equipment.read',
        ],
        memberCount: 1,
      },
      {
        key: 'OPERATOR',
        label: 'Opérateur',
        description: 'Accès aux missions explicitement assignées.',
        permissions: ['assigned_mission.read'],
        memberCount: 5,
      },
      {
        key: 'FIELD_MARKER',
        label: 'Installateur de balises',
        description: 'Interface terrain : voir et confirmer l’installation des balises.',
        permissions: ['marker.read', 'marker.update'],
        memberCount: 1,
      },
    ],
    permissions: permissionCatalog.map(([key, description]) => ({ key, description })),
    protectedPermissions,
  }
}

function initialAccessLog(): AccessLogEntry[] {
  return [
    {
      id: 'log-1',
      occurredAt: '2026-10-11T12:02:00.000Z',
      userId: 'user-op-1',
      identifier: 'e.cote-briere@groupereca.com',
      accountName: 'Élise Côté-Brière',
      eventType: 'LOGIN_SUCCEEDED',
      success: true,
      userAgent: 'Safari · iPad',
      source: 'APPLICATION',
    },
    {
      id: 'log-2',
      occurredAt: '2026-10-11T11:58:00.000Z',
      userId: 'user-op-1',
      identifier: 'e.cote-briere@groupereca.com',
      accountName: 'Élise Côté-Brière',
      eventType: 'LOGIN_FAILED',
      success: false,
      userAgent: 'Safari · iPad',
      source: 'LOGIN_SCREEN',
    },
    {
      id: 'log-3',
      occurredAt: '2026-10-11T09:15:00.000Z',
      userId: null,
      identifier: 'inconnu@exemple.ca',
      accountName: null,
      eventType: 'LOGIN_FAILED',
      success: false,
      userAgent: 'Chrome · Windows',
      source: 'LOGIN_SCREEN',
    },
    {
      id: 'log-4',
      occurredAt: '2026-10-10T21:30:00.000Z',
      userId: 'user-dispatch',
      identifier: 'c.gagnon@groupereca.com',
      accountName: 'Camille Gagnon',
      eventType: 'LOGOUT',
      success: true,
      userAgent: 'Firefox · macOS',
      source: 'APPLICATION',
    },
    {
      id: 'log-5',
      occurredAt: '2026-10-10T13:42:00.000Z',
      userId: 'user-dispatch',
      identifier: 'c.gagnon@groupereca.com',
      accountName: 'Camille Gagnon',
      eventType: 'LOGIN_SUCCEEDED',
      success: true,
      userAgent: 'Firefox · macOS',
      source: 'APPLICATION',
    },
  ]
}

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, ms)
    signal?.addEventListener('abort', () => {
      clearTimeout(timer)
      reject(new DOMException('Aborted', 'AbortError'))
    })
  })

export function createFixtureEmployeeRepository(options: { latencyMs?: number } = {}): EmployeeRepository {
  const latency = options.latencyMs ?? 250
  const equipments = initialEquipments()
  let employees = initialEmployees(equipments)
  const matrix = initialMatrix()
  const accessLog = initialAccessLog()
  const histories = new Map<string, EmployeeHistoryEntry[]>()
  let sequence = 0

  const taken = (identifier: string) => employees.some((employee) => employee.identifier === identifier)
  const byUser = (userId: string) => {
    const employee = employees.find((candidate) => candidate.userId === userId)
    if (!employee) throw new EmployeeCommandError('EMPLOYEE_NOT_FOUND')
    return employee
  }
  const record = (employee: EmployeeListItem, type: string, details: Record<string, unknown> = {}) => {
    sequence += 1
    const entries = histories.get(employee.key) ?? []
    entries.unshift({
      id: `h-${String(sequence)}`,
      occurredAt: new Date().toISOString(),
      type,
      details,
      actorName: 'Jean-François Roy',
    })
    histories.set(employee.key, entries)
  }
  const update = (key: string, patch: Partial<EmployeeListItem>) => {
    employees = employees.map((employee) => (employee.key === key ? { ...employee, ...patch } : employee))
  }
  // Routes ordonnées : la liste n'existe que pour les employés qui en ont reçu une; sinon la route principale.
  const rankedRoutes = new Map<string, string[]>()
  const routesOf = (employee: EmployeeListItem): EmployeeRoutes => {
    const ids = rankedRoutes.get(employee.key) ?? (employee.route ? [employee.route.id] : [])
    return {
      employeeId: employee.employeeId ?? employee.key,
      routes: ids.flatMap((id, index) => {
        const route = routeRef(id)
        return route ? [{ ...route, rank: index + 1 }] : []
      }),
    }
  }
  const activeAdministrators = () =>
    employees.filter((employee) => employee.status === 'ACTIVE' && employee.roles.includes('ADMINISTRATOR'))
      .length

  return {
    async list() {
      await wait(latency / 2)
      return employees.map((employee) => ({ ...employee }))
    },
    async listEquipments() {
      await wait(latency / 2)
      return equipments.map((item) => ({ ...item }))
    },
    async listAssignableRoutes() {
      await wait(latency / 2)
      return routes.map((route) => ({ ...route }))
    },
    async checkIdentifier(prenom, nom, signal) {
      await wait(latency, signal)
      const identifier = employeeIdentifier(prenom, nom)
      if (!identifier) throw new EmployeeCommandError('IDENTITY_INVALID')
      if (!taken(identifier)) return { identifier, available: true, suggestion: null }
      let suffix = 2
      while (suffix < 100 && taken(employeeIdentifier(prenom, nom, suffix) ?? '')) suffix += 1
      return { identifier, available: false, suggestion: employeeIdentifier(prenom, nom, suffix) }
    },
    async create(command) {
      await wait(latency)
      if (taken(command.identifier)) throw new EmployeeCommandError('IDENTIFIER_TAKEN')
      const key = `user-${command.idempotencyKey}`
      const route = command.routeId ? routeRef(command.routeId) : null
      const equipmentItem = equipments.find((item) => item.id === command.equipmentId)
      const created = person(key, command.prenom, command.nom, command.identifier, [command.roleKey], {
        lastLoginAt: null,
        mustChangePassword: true,
        route,
        equipment: equipmentItem ? equipmentRef(equipmentItem) : null,
      })
      employees = [...employees, created]
      record(created, 'EMPLOYEE_CREATED', { identifier: command.identifier, role: command.roleKey })
      return { identifier: command.identifier, employeeId: created.employeeId }
    },
    async setRole(userId, roleKey) {
      await wait(latency / 2)
      const employee = byUser(userId)
      if (
        employee.roles.includes('ADMINISTRATOR') &&
        roleKey !== 'ADMINISTRATOR' &&
        activeAdministrators() <= 1
      )
        throw new EmployeeCommandError('LAST_ADMINISTRATOR')
      const roles = [
        ...employee.roles.filter((role) => !(employeeBusinessRoles as readonly string[]).includes(role)),
        roleKey,
      ]
      update(employee.key, { roles })
      record(employee, 'ROLE_CHANGED', { before: employee.roles, after: roles })
    },
    async setActive(userId, active) {
      await wait(latency / 2)
      const employee = byUser(userId)
      if (userId === '00000000-0000-4000-b000-000000000001' && !active)
        throw new EmployeeCommandError('SELF_FORBIDDEN')
      if (!active && employee.roles.includes('ADMINISTRATOR') && activeAdministrators() <= 1)
        throw new EmployeeCommandError('LAST_ADMINISTRATOR')
      update(employee.key, { status: active ? 'ACTIVE' : 'DISABLED' })
      record(employee, active ? 'ACCOUNT_ENABLED' : 'ACCOUNT_DISABLED')
    },
    async resetPassword(userId) {
      await wait(latency)
      const employee = byUser(userId)
      if (userId === '00000000-0000-4000-b000-000000000001') throw new EmployeeCommandError('SELF_FORBIDDEN')
      update(employee.key, { mustChangePassword: true })
      record(employee, 'PASSWORD_RESET', { sessionsRevoked: true })
    },
    async revokeSessions(userId) {
      await wait(latency / 2)
      record(byUser(userId), 'SESSIONS_REVOKED')
    },
    async completeIdentity(userId, prenom, nom) {
      await wait(latency / 2)
      const employee = byUser(userId)
      if (employee.identityLocked) throw new EmployeeCommandError('IDENTITY_LOCKED')
      update(employee.key, {
        prenom,
        nom,
        displayName: `${prenom} ${nom}`,
        employeeId: `emp-${employee.key}`,
        identityLocked: true,
        identityToComplete: false,
      })
      record(employee, 'IDENTITY_COMPLETED')
    },
    async listEmployeeRoutes() {
      await wait(latency / 2)
      return employees.filter((employee) => employee.employeeId).map(routesOf)
    },
    async setEmployeeRoutes(employeeId, routeIds) {
      await wait(latency / 2)
      const employee = employees.find((candidate) => candidate.employeeId === employeeId)
      if (!employee) throw new EmployeeCommandError('EMPLOYEE_NOT_FOUND')
      if (new Set(routeIds).size !== routeIds.length) throw new EmployeeCommandError('ROUTE_DUPLICATE')
      if (routeIds.some((id) => !routeRef(id))) throw new EmployeeCommandError('ROUTE_UNAVAILABLE')
      const before = routesOf(employee)
        .routes.map((route) => route.name)
        .join(' → ')
      rankedRoutes.set(employee.key, [...routeIds])
      const first = routeIds[0]
      update(employee.key, { route: first ? routeRef(first) : null })
      const after = employees.find((candidate) => candidate.key === employee.key) ?? employee
      const next = routesOf(after)
      record(employee, 'ROUTE_CHANGED', {
        previous: before || null,
        next: next.routes.map((route) => route.name).join(' → ') || null,
      })
      return next
    },
    async setAssignment(employeeId, kind, resourceId) {
      await wait(latency / 2)
      const employee = employees.find((candidate) => candidate.employeeId === employeeId)
      if (!employee) throw new EmployeeCommandError('EMPLOYEE_NOT_FOUND')
      if (kind === 'ROUTE') {
        const next = resourceId ? routeRef(resourceId) : null
        if (resourceId && !next) throw new EmployeeCommandError('ROUTE_UNAVAILABLE')
        record(employee, 'ROUTE_CHANGED', {
          previous: employee.route?.name ?? null,
          next: next?.name ?? null,
        })
        update(employee.key, { route: next })
        // Voie historique « une route » : la nouvelle principale prend le rang 1, les suivantes restent (serveur idem).
        const list = rankedRoutes.get(employee.key)
        if (list) {
          const previousFirst = list[0]
          rankedRoutes.set(
            employee.key,
            resourceId ? [resourceId, ...list.filter((id) => id !== resourceId && id !== previousFirst)] : [],
          )
        }
        return
      }
      const item = equipments.find((candidate) => candidate.id === resourceId)
      if (resourceId && !item?.available) throw new EmployeeCommandError('EQUIPMENT_UNAVAILABLE')
      const label = (value: { number: string | null; name: string } | null | undefined) =>
        value ? [value.number, value.name].filter(Boolean).join(' · ') : null
      record(employee, 'EQUIPMENT_CHANGED', { previous: label(employee.equipment), next: label(item) })
      update(employee.key, { equipment: item ? equipmentRef(item) : null })
    },
    async history(userId, employeeId) {
      await wait(latency / 2)
      const employee = employees.find(
        (candidate) => candidate.userId === userId || candidate.employeeId === employeeId,
      )
      return employee ? [...(histories.get(employee.key) ?? [])] : []
    },
    async accessLog(filters) {
      await wait(latency / 2)
      const filtered = accessLog.filter(
        (entry) =>
          (!filters.userId || entry.userId === filters.userId) &&
          (!filters.result || (filters.result === 'SUCCESS') === entry.success) &&
          (!filters.from || entry.occurredAt >= filters.from) &&
          (!filters.to || entry.occurredAt < filters.to),
      )
      return { total: filtered.length, items: filtered.slice(filters.offset, filters.offset + filters.limit) }
    },
    async rolesMatrix() {
      await wait(latency / 2)
      return structuredClone(matrix)
    },
    async setRolePermission(roleKey, permissionKey, granted) {
      await wait(latency / 2)
      if (roleKey === 'ADMINISTRATOR' && !granted && protectedPermissions.includes(permissionKey))
        throw new EmployeeCommandError('PROTECTED_PERMISSION')
      const role = matrix.roles.find((candidate) => candidate.key === roleKey)
      if (!role) throw new EmployeeCommandError('INVALID_COMMAND')
      role.permissions = granted
        ? [...new Set([...role.permissions, permissionKey])]
        : role.permissions.filter((key) => key !== permissionKey)
    },
    async createEquipment(input) {
      await wait(latency / 2)
      if (input.number && equipments.some((item) => item.number === input.number))
        throw new EmployeeCommandError('EQUIPMENT_NUMBER_TAKEN')
      sequence += 1
      equipments.push({
        id: `eq-new-${String(sequence)}`,
        number: input.number ?? `EQ-${String(200 + sequence)}`,
        name: input.name,
        category: input.category,
        status: 'disponible',
        active: true,
        available: true,
      })
    },
    async setEquipmentActive(equipmentId, active) {
      await wait(latency / 2)
      const item = equipments.find((candidate) => candidate.id === equipmentId)
      if (!item) throw new EmployeeCommandError('EQUIPMENT_NOT_FOUND')
      item.active = active
      item.available = active && item.status !== 'brise' && item.status !== 'entretien'
      // Les affectations existantes ne sont jamais remplacées : elles passent « à corriger ».
      employees = employees.map((employee) =>
        employee.equipment?.id === equipmentId ? { ...employee, equipment: equipmentRef(item) } : employee,
      )
    },
  }
}
